import { Box, Stack, Typography } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { Switch } from '@/components/form/baseInputs/Switch';
import { Card } from '@/components/layout/Card/Card';
import { Callout } from '@/components/notifications/Callout/Callout';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useReportError } from '@/hooks/useReportError';
import {
  getOrionIntegrationLoginPath,
  getOrionIntegrationLogoutPath,
} from '@/modules/assetProviderIntegrations/orion/utils/orionAuthUtils';
import { useTenantDetailsContext } from '@/modules/tenant/TenantDetailsContext/TenantDetailsContext';
import { getCompletePathFromRouteKey } from '@/navigation/navigationUtils';
import { getPublicImageUrl } from '@/utils/staticFiles';

import {
  AdminIntegrationsPage_IntegrationConfigurationFragment,
  useUpdateIntegrationConfigurationMutation,
} from './graphql/AdminIntegrationsPage.generated';
import { IntegrationUserSetupModal } from './IntegrationUserSetupModal';
import { ResyncTenantValuationsButton } from './ResyncTenantValuationsButton/ResyncTenantValuationsButton';

export interface OrionIntegrationCardProps {
  isLoggedIn: boolean;
  externalUserName?: string | null;
  configuration: AdminIntegrationsPage_IntegrationConfigurationFragment | null;
  disabled?: boolean;
}

export function OrionIntegrationCard({
  isLoggedIn,
  externalUserName,
  configuration,
  disabled = false,
}: OrionIntegrationCardProps) {
  const { integrationConfiguration } = useTenantDetailsContext();
  const { createSuccessFeedback, showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const [isOrionEnabled, setIsOrionEnabled] = useState(
    integrationConfiguration?.orionEnabled ?? false
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [updateConfiguration] = useUpdateIntegrationConfigurationMutation({
    onCompleted: createSuccessFeedback(
      'Orion configuration updated successfully'
    ),
    onError: (err) => {
      showFeedback('Failed to update Orion configuration');
      reportError('Failed to update Orion configuration', err);
    },
  });

  const currentPageURL = getCompletePathFromRouteKey('ADMIN_INTEGRATIONS', {});

  const queryParams = useMemo(
    () => ({
      is_integration_user: 'true',
      redirect: currentPageURL,
    }),
    [currentPageURL]
  );

  async function handleToggleOrion() {
    if (!configuration) {
      throw new Error('Configuration is not available');
    }

    setIsOrionEnabled(!isOrionEnabled);
    await updateConfiguration({
      variables: {
        input: {
          id: configuration.id,
          update: {
            orionEnabled: !isOrionEnabled,
          },
        },
      },
    });
  }

  const getOrionButtonProps = useCallback(() => {
    if (isLoggedIn) {
      return {
        children: 'Disconnect integration user',
        variant: 'secondary' as const,
        href: getOrionIntegrationLogoutPath(queryParams),
        method: 'post' as const,
      };
    } else {
      return {
        children: 'Connect integration user',
        variant: 'primary' as const,
        onClick: () => {
          setIsModalOpen(true);
        },
        disabled: !isOrionEnabled,
      };
    }
  }, [isOrionEnabled, isLoggedIn, queryParams]);

  return (
    <Stack position="relative">
      {isLoggedIn && (
        <Box position="absolute" right={-5} top={-5}>
          <ResyncTenantValuationsButton />
        </Box>
      )}
      <Box textAlign="center">
        <img
          alt="Orion"
          src={getPublicImageUrl('/orionLogo.jpg')}
          height={40}
        />
      </Box>
      <Stack mb={2} mt={1} spacing={2}>
        <Typography variant="body-s">
          Manage the integration user for your Orion account. This user should
          have access to all clients and portfolio data to enable regular
          syncing of information between systems.
          <br />
        </Typography>
        <Card variant="filled" sx={{ p: 2 }}>
          <Switch
            labelPosition="right"
            value={isOrionEnabled}
            label="Enable Orion"
            onChange={() => handleToggleOrion()}
            disabled={disabled}
          />
        </Card>
      </Stack>
      <Box mt={2} mb={2} textAlign="center">
        <Button
          size="sm"
          target="_self"
          {...getOrionButtonProps()}
          fullWidth
          external={isLoggedIn} // Only external if it's the disconnect button
          disabled={disabled || getOrionButtonProps().disabled}
        />
      </Box>

      {isLoggedIn && externalUserName && (
        <Callout severity="info-low">Linked using {externalUserName}</Callout>
      )}

      <IntegrationUserSetupModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onContinue={() => {
          setIsModalOpen(false);
          window.location.href = getOrionIntegrationLoginPath(queryParams);
        }}
        providerName="Orion"
      />
    </Stack>
  );
}
